import { t } from "i18next";
import { ChangeEvent, FC } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";

import Checkbox from "@app/components/inputs/Checkbox";
import TextInput from "@app/components/inputs/Text";
import Toggle from "@app/components/inputs/Toggle";
import { SystemConfigurationErrors } from "@app/types/Common.types";
import { SystemConfigurationFormData } from "@app/types/SystemConfiguration";
import { BULK_AUTO_ACTION_MODE, FEATURE_CHECKBOXES } from "@app/utils/constant";

interface SystemConfigurationFormProps {
    data: SystemConfigurationFormData;
    errors?: Partial<SystemConfigurationErrors>;
    onChange: (_name: string, _value: string | boolean) => void;
}

const SystemConfigurationForm: FC<SystemConfigurationFormProps> = ({ data, errors, onChange }) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        onChange(name, value);
    };
    const isAutoProcess = data?.bulkAutoActionMode === BULK_AUTO_ACTION_MODE.autoProcess;

    return (
        <Container fluid className='py-4 px-5'>
            <Row className='mb-4'>
                <Col xs={6}>
                    <TextInput
                        type='number'
                        label={`${t("SYSTEMCONFIGURATION.DELAYTIME")}`}
                        placeholder='Enter Delay Time'
                        name='delayTime'
                        onChange={handleChange}
                        value={data.delayTime}
                        error={errors?.delayTime}
                        twoDigitAllowed={true}
                    />
                </Col>
                <Col xs={6}>
                    <TextInput
                        type='number'
                        label='Machines for Normal Orders'
                        placeholder='Enter Percentage'
                        name='bulkBuffer'
                        onChange={handleChange}
                        value={data?.bulkBuffer ?? "0"}
                        disabled={!data.bulk}
                        error={errors?.bulkBuffer}
                        threeDigitAllowed
                    />
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={6}>
                    <TextInput
                        type='number'
                        label='Threshold for HT'
                        placeholder='Enter Percentage'
                        name='HTThreshold'
                        onChange={handleChange}
                        value={data?.HTThreshold ?? "100"}
                        disabled={!data.optimizeClubbing}
                        error={errors?.HTThreshold}
                        threeDigitAllowed
                    />
                </Col>
            </Row>
            <hr className='divider' />
            <Row className='mb-5'>
                <Col>
                    <Form.Group>
                        <Form.Label className='text-input__label mb-4 no-margin'>
                            Select Features
                        </Form.Label>
                        <div className='select-feature'>
                            {FEATURE_CHECKBOXES?.map(({ key, label }) => (
                                <Col sm={3} key={key}>
                                    {" "}
                                    <Checkbox
                                        className='checkbox-container'
                                        checked={data[key] as boolean}
                                        label={label}
                                        onClick={() => onChange(key, !(data[key] as boolean))}
                                    />
                                </Col>
                            ))}
                        </div>
                    </Form.Group>
                </Col>
            </Row>
            <hr className='divider' />
            <Row>
                <Col xs={6}>
                    <Toggle
                        display={
                            isAutoProcess
                                ? `${t("COMMON.AUTO_PROCESS")}`
                                : `${t("COMMON.AUTO_REJECT")}`
                        }
                        isActive={isAutoProcess ? true : false}
                        onToggle={() =>
                            onChange(
                                "bulkAutoActionMode",
                                isAutoProcess
                                    ? BULK_AUTO_ACTION_MODE.autoReject
                                    : BULK_AUTO_ACTION_MODE.autoProcess,
                            )
                        }
                        heading={`${t("SYSTEMCONFIGURATION.AUTOACTION")}`}
                        isDisabled={!data.globalEPT}
                    />
                </Col>
                <Col xs={6}>
                    <TextInput
                        type='number'
                        label={`${t("SYSTEMCONFIGURATION.AUTOACTIONTHRUSHHOLD")}`}
                        placeholder='0'
                        name='bulkAutoActionThreshold'
                        onChange={handleChange}
                        value={data.bulkAutoActionThreshold}
                        error={errors?.bulkAutoActionThreshold}
                        twoDigitAllowed={true}
                        unitLabel={`${t("COMMON.MINUTES")}`}
                        disabled={!data.globalEPT}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default SystemConfigurationForm;
